import { SessionPlayer } from '@/components/session/session-player';
import { BUBBLE_GRACE_MS, IDLE_CONVERSATION, sessionThread } from '@/lib/session-conversation';
import { useElapsed } from '@/lib/use-elapsed';
import { useReaderFormat } from '@/lib/use-reader-format';
import type { SessionView } from '@matter_extractor/shared';

/**
 * L'écran de la personne, en lecture seule (npc.md §11, N11, H17 ; maquette `Regarder`) : LE lecteur
 * de la session - `SessionPlayer`, donc le thème, les pages, les temps, les bulles et le fil de la
 * conversation - monté sur la vue qu'on lui donne, que ce soit celle que le serveur relit en direct
 * ou celle que la lecture en différé a remontée à un instant.
 *
 * Rien n'y est recalculé : la vue est celle du serveur (`sessionViewOf`, publique, sans rien de la
 * machine), le fil est celui de `sessionThread`, et ce composant n'ajoute que ce qu'un spectateur
 * n'a pas - aucune réponse, aucune avancée, aucun envoi. Le champ de la conversation est dessiné
 * comme chez la personne, inerte, et dit pourquoi (`note`).
 *
 * L'interviewer qui écrit se voit : quand le dernier mot est celui du joueur et que la session est en
 * cours, la bulle de points est levée, comme chez la personne entre l'envoi et le premier mot.
 */

export interface WatchScreenProps {
  view: SessionView;
  /** The sentence the inert field carries. */
  note: string;
  /** Playback only: how many temps of the page are open. Absent, the page plays its own. */
  revealed?: number;
  /** A recorded sequence waits for nothing: the elements of a page come with it. */
  recorded?: boolean;
}

export function WatchScreen({ view, note, revealed, recorded = false }: WatchScreenProps) {
  const format = useReaderFormat();
  const probe = sessionThread(view, IDLE_CONVERSATION, recorded);
  const pageKey = view.currentPageId ?? '';
  const gaveUp = useElapsed(probe.withheld && !recorded, pageKey, BUBBLE_GRACE_MS) || recorded;
  const threaded = sessionThread(view, IDLE_CONVERSATION, gaveUp);
  const messages = view.conversation ?? [];
  const last = messages[messages.length - 1];
  const typing =
    view.protocolMode === 'conversation' &&
    view.status === 'in-progress' &&
    threaded.state === 'step' &&
    last !== undefined &&
    last.role === 'person';

  return (
    <SessionPlayer
      view={view}
      format={format}
      advancing={false}
      refusal={null}
      onAdvance={() => undefined}
      conversation={{
        thread: threaded.thread,
        typing,
        state: threaded.state,
        busy: false,
        refusal: null,
        onSend: () => Promise.resolve(false),
      }}
      watching={{ note, ...(revealed === undefined ? {} : { revealed }) }}
    />
  );
}
